import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getBudget, createBudget, updateBudget, getBudgetFormData, deleteBudget } from '../../../models/budgetsModel';

function BudgetForm({ isOpen, onClose, budgetId = null, initialValues = null }) {
  const [formData, setFormData] = useState({
    year: initialValues?.year || new Date().getFullYear(),
    gl_code: initialValues?.gl_code || '',
    month: initialValues?.month || '',
    amount: '',
    items: '',
    item_amounts: [] // Store individual item amounts
  });
  
  const [glCodes, setGlCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, budgetId]);

  useEffect(() => {
    if (initialValues && !budgetId) {
      setFormData(prev => ({
        ...prev,
        year: initialValues.year || prev.year,
        gl_code: initialValues.gl_code || prev.gl_code,
        month: initialValues.month || prev.month
      }));

      // If GL code from initialValues is not in the glCodes list, add it
      if (initialValues.gl_code && glCodes.length > 0) {
        const glExists = glCodes.some(gl => gl.gl_code === initialValues.gl_code);
        if (!glExists) {
          // Add the GL code to the list with the gl_code as description
          setGlCodes(prev => [...prev, {
            gl_code: initialValues.gl_code,
            description: initialValues.description || initialValues.gl_code
          }]);
        }
      }
    }
  }, [initialValues, budgetId, glCodes.length]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      let workflows = [];

      // Fetch GL Codes
      try {
        const formDataResponse = await getBudgetFormData();
        // Handle nested data structure: { status: 1, data: { workflows: [...] } }
        workflows = formDataResponse?.data?.workflows || formDataResponse?.workflows || [];
      } catch (e) {
        console.warn("Could not fetch form data", e);
      }
      
      if (workflows.length > 0) {
        setGlCodes(workflows);
      }

      // If editing, fetch budget details
      if (budgetId) {
        const budgetResponse = await getBudget(budgetId);
        // Handle nested data structure: { status: 1, data: { items: [...], workflows: [...] } }
        const responseData = budgetResponse?.data || budgetResponse || {};
        const items = responseData.items || [];
        
        // The edit response might also contain workflows
        const budgetWorkflows = responseData.workflows || [];
        if (budgetWorkflows.length > 0) {
            setGlCodes(budgetWorkflows);
        }

        if (items.length > 0) {
          const item = items[0];
          
          // Map month name to value if necessary
          let monthValue = item.month;
          if (isNaN(monthValue)) {
             const monthObj = months.find(m => m.label.toLowerCase() === String(item.month).toLowerCase());
             if (monthObj) {
                 monthValue = monthObj.value;
             }
          }

          setFormData({
            year: item.year,
            gl_code: item.gl,
            month: monthValue,
            amount: item.amount,
            items: items.map(i => i.budget_item_name).join('\n'),
            item_amounts: items.map(i => i.budget_amount || 0) // Preserve item amounts
          });
        }
      } else {
        // Reset form for create
        setFormData({
          year: new Date().getFullYear(),
          gl_code: '',
          month: '',
          amount: '',
          items: '',
          item_amounts: []
        });
      }
    } catch (err) {
      console.error('Error loading form data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDelete = async () => {
    if (!budgetId) return;
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this budget month and all associated items. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await deleteBudget(budgetId);
        toast.success('Budget deleted successfully!');
        onClose();
      } catch (error) {
        console.error('Error deleting budget:', error);
        toast.error(error.response?.data?.message || 'Failed to delete budget. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        gl: formData.gl_code, // Backend expects 'gl'
        id: budgetId
      };
// Include item_amounts when updating to preserve allocations
      if (budgetId && formData.item_amounts && formData.item_amounts.length > 0) {
        submitData.item_amounts = JSON.stringify(formData.item_amounts);
      }

      
      if (budgetId) {
        await updateBudget(budgetId, submitData);
        toast.success('Budget updated successfully!');
      } else {
        await createBudget(submitData);
        toast.success('Budget created successfully!');
      }

      onClose();
    } catch (err) {
      console.error('Error saving budget:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save budget. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h3 className="text-lg font-medium text-gray-900">
            {budgetId ? 'Update Budget' : 'Create New Budget'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Left Column: Inputs */}
              <div className="space-y-4">
                {/* GL Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GL Code</label>
                  <select
                    name="gl_code"
                    value={formData.gl_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                  >
                    <option value="">Select GL Code</option>
                    {glCodes.map(gl => (
                      <option key={gl.gl_code} value={gl.gl_code}>
                        {gl.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    name="month"
                    value={formData.month}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                  >
                    <option value="">Select</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                  >
                    {[...Array(15)].map((_, i) => {
                      // Start from 2020 to cover past budgets
                      const year = 2020 + i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">R</span>
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column: Items */}
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-1">Items</label>
                <textarea
                  name="items"
                  value={formData.items}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm flex-grow text-gray-900"
                  placeholder="Please enter items in a list going down."
                  style={{ minHeight: '300px' }}
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center">
              {budgetId ? (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
                >
                  Delete Budget
                </button>
              ) : (
                <div></div>
              )}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default BudgetForm;
